import { createRouter, createWebHistory } from 'vue-router';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);
import MainPage from '@/views/MainPage.vue';

import siteData from '@/data/sitemap.json';
import Lenis from 'lenis';

import NotFoundPage from '@/views/common/NotFoundPage.vue';
import PrivacyPage from '@/views/PrivacyPage.vue';
import EmploymentRetrieve from '@/views/recruit/EmploymentRetrieve.vue';
import GuidePage from '@/views/GuidePage.vue';
import DigitalServicePage from '@/views/DigitalServicePage.vue';
import ComponentList from '@/views/ComponentList.vue';
import Apply from '@/views/recruit/resume/applyPage.vue';

export const lenis = new Lenis();

if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual';
}

lenis.on('scroll', ScrollTrigger.update);

gsap.ticker.add((time) => {
    lenis.raf(time * 1000);
});
gsap.ticker.lagSmoothing(0);

const components = {};
const requireComponent = require.context('@/views', true, /\.vue$/);

requireComponent.keys().forEach((fileName) => {
    const componentConfig = requireComponent(fileName);
    const componentName = fileName
        .split('/')
        .pop()
        .replace(/\.\w+$/, '');
    components[componentName] = componentConfig.default || componentConfig;
});

const platSubmenu = (path, obj, parent, depth = 0) => {
    return obj.map((menu) => {
        if (menu.tabMenus) {
            return {
                parent: parent,
                depth: depth,
                subVisualSrc: parent.subVisualSrc,
                sitemap: [...(parent.meta?.sitemap || []), { title: parent.name, titleEn: parent.nameEn, link: parent.path || path }],
            };
        }
        if (menu.subMenus) {
            menu.meta = {
                parent: parent,
                depth: depth,
                subVisualSrc: parent.subVisualSrc,
                sitemap: [...(parent.meta?.sitemap || []), { title: parent.name, titleEn: parent.nameEn, link: parent.path || path }],
            };
            return platSubmenu(`${path}/${menu.key}`, menu.subMenus, menu, depth + 1);
        } else {
            const originRoute = {
                component: components[menu.compKey] || components['TempleatPage'],
                path: `${path}/${menu.key}`,
                name: menu.key + menu.compKey,
                meta: {
                    parent: parent,
                    depth: depth,
                    subVisualSrc: parent.meta?.subVisualSrc || parent.subVisualSrc,
                    name: menu.name,
                    nameEn: menu.nameEn,
                    sitemap: [...(parent.meta?.sitemap || []), { title: parent.name, titleEn: parent.nameEn, link: parent.path || path }],
                },
            };

            if (menu.children) {
                return [
                    originRoute,
                    {
                        ...originRoute,
                        name: originRoute.name + menu.children.key + menu.children.compKey,
                        path: `${path}/${menu.key}/:${menu.children.key}`,
                        component: components[menu.children.compKey],
                    },
                ];
            } else {
                return originRoute;
            }
        }
    });
};

const routes = Object.keys(siteData).reduce((acc, cur) => {
    const route = { ...siteData[cur], key: cur };

    const sub = platSubmenu(`/${cur}`, route.subMenus, route).flat().flat();
    sub.forEach((el) => acc.push(el));

    return acc;
}, []);

const FORCE_TOP_SCROLL_PATHS = new Set(['/privacy', '/digital', '/guide', '/company/location', '/cs/voc']);

const router = createRouter({
    history: createWebHistory(''),
    scrollBehavior(to) {
        if (FORCE_TOP_SCROLL_PATHS.has(to.path)) {
            return { left: 0, top: 0 };
        }
    },
    routes: [
        {
            path: '/',
            name: 'main',
            component: MainPage,
            meta: {
                name: '한국정보통신(주)',
            },
        },
        ...routes,
        {
            path: '/privacy',
            name: 'privacy',
            meta: {
                name: '개인정보처리방침',
            },
            component: PrivacyPage,
        },
        {
            path: '/guide',
            name: 'guide',
            meta: {
                name: '이용약관',
            },
            component: GuidePage,
        },
        {
            path: '/digital',
            name: 'digital',
            meta: {
                name: '디지털 상품권 서비스',
            },
            component: DigitalServicePage,
        },
        {
            path: '/recruit/employment_retrieve',
            name: 'employmentretrieve',
            meta: {
                name: '입사지원 조회',
            },
            component: EmploymentRetrieve,
        },
        {
            path: '/recruit/resume/apply',
            name: 'apply',
            meta: {
                name: '지원서 작성',
            },
            component: Apply,
        },
        {
            path: '/componentList',
            name: 'componentList',
            component: ComponentList,
        },
        {
            path: '/:pathMatch(.*)*',
            name: 'NotFound',
            component: NotFoundPage,
        },
    ],
});

router.beforeEach((to, from, next) => {
    // ProductList에서 떠날 때 스크롤 위치 저장
    if (from.path && from.path.match(/^\/product\/(pos|kiosk|card_terminal|signpad|etc)$/)) {
        sessionStorage.setItem('productListScrollPosition', lenis.actualScroll.toString());
    }
    
    if (from.name === 'dataDataPage' && to.name === 'dataDataPageidDataPostView') {
        next();
    }
    next();
});

router.afterEach((to, from) => {
    if (from.path === to.path) {
        return;
    }

    document.querySelector('header')?.removeAttribute('class');
    lenis.start();
    
    // ProductList로 돌아올 때만 저장된 스크롤 위치 확인
    const isProductListPath = to.path && to.path.match(/^\/product\/(pos|kiosk|card_terminal|signpad|etc)$/);
    const wasProductDetailPage = from.path && from.path.match(/^\/product\/(pos|kiosk|card_terminal|signpad|etc)\/[^/]+$/);
    
    if (FORCE_TOP_SCROLL_PATHS.has(to.path)) {
        setTimeout(() => {
            window.scrollTo(0, 0);
            lenis.scrollTo(0, { duration: 0, immediate: true });
        }, 0);
    } else if (isProductListPath && wasProductDetailPage && sessionStorage.getItem('productListScrollPosition')) {
        const savedPosition = parseInt(sessionStorage.getItem('productListScrollPosition'));
        setTimeout(() => {
            lenis.scrollTo(savedPosition, { duration: 0 });
            sessionStorage.removeItem('productListScrollPosition');
        }, 100);
    } else {
        lenis.scrollTo(0);
    }
});

export default router;
